import type { OutreachCard } from '../../services/dataService';
import { daysSince } from '../../utils/time';

/**
 * Where a deal stands, in one line, and what needs the creator — shared by the Bearri tab's deal
 * list and the dashboard's Deals glance, so the two can never describe the same deal two ways.
 * Every word comes off the server row: its stage, its dates, and a reply's classified intent
 * (absent until classified — never an "unclear" badge we did not earn).
 */

export const FOLLOW_UP_AFTER_DAYS = 4;

export const INTENT_LABEL: Record<string, string> = {
  interested: 'interested', rates: 'asking your rates', needs_info: 'wants more info',
  pass: 'passed', auto_reply: 'auto-reply', unclear: 'needs a read',
};

/** What a reply is asking for — the goal handed to Bearri when the creator asks for help answering. */
export const INTENT_GOAL: Record<string, string> = {
  interested: 'keep the momentum and propose a concrete next step',
  rates: 'give clear pricing and what they get for it',
  needs_info: 'send the proof they asked for and make it easy to say yes',
  pass: 'leave the door open for a later campaign without pushing',
  auto_reply: 'reach a real person on the partnerships team',
  unclear: 'answer them and keep it moving',
};

const ago = (days: number | null) => (days == null ? '' : days <= 0 ? 'today' : `${days}d ago`);

export type DealTone = 'reply' | 'warn' | 'wait' | 'draft' | 'won' | 'lost';

/** One line for a deal, and the tone of the edge its row is marked with (colour = state). */
export function dealStatus(card: OutreachCard): { line: string; tone: DealTone; needsYou: boolean } {
  switch (card.stage) {
    case 'replied': {
      const intent = card.reply_intent ? INTENT_LABEL[card.reply_intent] : '';
      const when = ago(daysSince(card.last_inbound_at || card.last_reply_at));
      return { line: ['Replied', when].filter(Boolean).join(' ') + (intent ? ` — ${intent}` : ''), tone: 'reply', needsYou: true };
    }
    case 'bounced':
      return { line: 'Bounced — find a better contact', tone: 'warn', needsYou: true };
    case 'delivery_check':
      return { line: 'Sent? Check delivery before anything else', tone: 'warn', needsYou: true };
    case 'awaiting': {
      const d = daysSince(card.sent_at);
      if (d != null && d >= FOLLOW_UP_AFTER_DAYS) return { line: `No reply in ${d}d — follow up`, tone: 'warn', needsYou: true };
      return { line: d == null ? 'Sent — waiting for a reply' : `Sent ${ago(d)} — waiting for a reply`, tone: 'wait', needsYou: false };
    }
    case 'drafted': {
      const d = daysSince(card.created_at);
      return { line: `Draft${d != null ? ` from ${ago(d)}` : ''} — not sent yet`, tone: 'draft', needsYou: false };
    }
    case 'won':
      return { line: typeof card.deal_amount === 'number'
        ? `Won · $${card.deal_amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}${card.deal_deliverables ? ` · ${card.deal_deliverables}` : ''}`
        : 'Won', tone: 'won', needsYou: false };
    default:
      return { line: 'Didn’t land', tone: 'lost', needsYou: false };
  }
}

/** A deal's line spelled out for reading, the one way the Bearri page prints it (the drawer's rows and the case
 *  head): "Draft from 12d ago — not sent yet" → "Draft from 12 days ago · not sent yet". The dashboard's glance
 *  keeps dealStatus()'s compact line. */
export function spelledStatus(line: string): string {
  return line.replace(/ — /g, ' · ').replace(/\b(\d+)d\b/g, (_, n: string) => `${n} day${n === '1' ? '' : 's'}`);
}

/** The order a stage needs attention in: a human answered, then what is broken, then what went quiet. */
const URGENCY: Partial<Record<OutreachCard['stage'], number>> = { replied: 0, delivery_check: 1, bounced: 2, awaiting: 3 };

/** The deal list's groups, each in the order it should be read. Empty groups are left out. */
export function railGroups(cards: OutreachCard[]) {
  const needs = cards.filter(c => dealStatus(c).needsYou);
  const waiting = cards.filter(c => c.stage === 'awaiting' && !dealStatus(c).needsYou);
  const newest = (at: (c: OutreachCard) => string | null | undefined) => (a: OutreachCard, b: OutreachCard) =>
    (new Date(at(b) || 0).getTime()) - (new Date(at(a) || 0).getTime());
  needs.sort((a, b) => (URGENCY[a.stage] ?? 9) - (URGENCY[b.stage] ?? 9)
    // a reply: the newest first; a quiet pitch: the one waiting longest first
    || (a.stage === 'replied' ? newest(c => c.last_inbound_at || c.last_reply_at)(a, b) : newest(c => c.sent_at)(b, a)));
  const groups = [
    { key: 'needs', title: 'Needs you', cards: needs },
    { key: 'waiting', title: 'Waiting on them', cards: waiting.sort(newest(c => c.sent_at)) },
    { key: 'drafts', title: 'Drafts', cards: cards.filter(c => c.stage === 'drafted').sort(newest(c => c.created_at)) },
    { key: 'won', title: 'Won', cards: cards.filter(c => c.stage === 'won').sort(newest(c => c.outcome_at)) },
    { key: 'lost', title: 'Didn’t land', cards: cards.filter(c => c.stage === 'lost').sort(newest(c => c.outcome_at)) },
  ];
  return groups.filter(g => g.cards.length > 0);
}

/** The few deals that need the creator, most urgent first — the dashboard shows these. A draft
 *  that was never sent counts, last, so a quiet week still points somewhere. */
export function needsYou(cards: OutreachCard[], limit = 3): OutreachCard[] {
  const urgent = railGroups(cards).find(g => g.key === 'needs')?.cards || [];
  const drafts = cards.filter(c => c.stage === 'drafted')
    .sort((a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime());
  return [...urgent, ...drafts].slice(0, limit);
}

/** The Bearri tab, opened on one deal. */
export const dealHref = (id: string) => `/app/bearri?deal=${encodeURIComponent(id)}`;

/** A deal's Offer & terms (the Deal Room, /app/deals/:pitchId). With a read id it opens on that one read (a Reports-shelf
 *  row); without, on the newest. */
export const dealDeskHref = (id: string, readId?: string | null) =>
  `/app/deals/${encodeURIComponent(id)}${readId ? `?read=${encodeURIComponent(readId)}` : ''}`;

/** Where an old link lands. The Deals list (/app/outreach?pitch= or ?open=) and the assistant page
 *  (/app/assistant?deal= or ?thread=) both became the Bearri tab; reply-alert emails and background
 *  job notifications still carry the old addresses, so each keeps opening the same deal or conversation. */
export function bearriPathFor(search: string): string {
  const from = new URLSearchParams(search);
  const next = new URLSearchParams();
  const deal = from.get('deal') || from.get('pitch') || from.get('open');
  const thread = from.get('thread');
  if (deal) next.set('deal', deal);
  else if (thread) next.set('thread', thread);
  const query = next.toString();
  return `/app/bearri${query ? `?${query}` : ''}`;
}
